import * as types from './actionTypes';
import {
  getCurrentSubStep,
  GuideDescription,
  OnGoingGuide,
} from 'data/ui/guide/Guide';
import guideList from '../../../../assets/json/guide/guides.json';
import { UndefinedAble } from 'data/base';

export interface GuideState {
  guides: GuideDescription[];
  onGoingGuide: UndefinedAble<OnGoingGuide>;
}

const initialState: GuideState = {
  guides: guideList as GuideDescription[],
  onGoingGuide: undefined,
};

type ActionDelegate<T extends types.GuideAction> = (
  state: GuideState,
  action: T
) => GuideState;

const actionDelegateMap: { [key: string]: ActionDelegate<any> } = {
  [types.SELECT_GUIDE as string]: selectGuide as ActionDelegate<
    types.SelectGuideAction
  >,
  [types.COMPLETE_GUIDE as string]: forwardGuideStep as ActionDelegate<
    types.CompleteGuideAction
  >,
  [types.STOP_GUIDE as string]: stopGuide as ActionDelegate<
    types.StopGuideAction
  >,
  [types.MARK_GUI_READY as string]: markGUIReady as ActionDelegate<
    types.MarkGUIReadyAction
  >,
};

export default function reduce(
  state: GuideState = initialState,
  action: types.GuideAction
): GuideState {
  const delegate = actionDelegateMap[action.type];
  if (delegate) {
    return delegate(state, action);
  } else {
    return state;
  }
}

function selectGuide(
  state: GuideState,
  action: types.SelectGuideAction
): GuideState {
  return {
    ...state,
    onGoingGuide: {
      ...action.guide,
      currentStep: 0,
      currentSubStep: 0,
      isGUIReady: false,
    },
  };
}

export const SELECTOR_MATCH_ALL = 'SELECTOR_MATCH_ALL';

function markGUIReady(
  state: GuideState,
  action: types.MarkGUIReadyAction
): GuideState {
  if (state.onGoingGuide) {
    const currentSubStep = getCurrentSubStep(state.onGoingGuide);
    if (
      action.selectors === SELECTOR_MATCH_ALL ||
      action.selectors.includes(currentSubStep.selector)
    ) {
      return {
        ...state,
        onGoingGuide: {
          ...state.onGoingGuide,
          isGUIReady: true,
        },
      };
    }
  }

  return state;
}

function forwardGuideStep(
  state: GuideState,
  action: types.CompleteGuideAction
): GuideState {
  if (state.onGoingGuide) {
    const currentSubStep = getCurrentSubStep(state.onGoingGuide);
    if (currentSubStep.actionToComplete !== action.actionDone) {
      return state;
    }

    const nextSubStep = state.onGoingGuide.currentSubStep + 1;
    if (
      nextSubStep <
      state.onGoingGuide.steps[state.onGoingGuide.currentStep].subSteps.length
    ) {
      return {
        ...state,
        onGoingGuide: {
          ...state.onGoingGuide,
          currentSubStep: nextSubStep,
          isGUIReady: false,
        },
      };
    } else {
      const nextStep = state.onGoingGuide.currentStep + 1;
      if (nextStep < state.onGoingGuide.steps.length) {
        return {
          ...state,
          onGoingGuide: {
            ...state.onGoingGuide,
            currentStep: nextStep,
            currentSubStep: 0,
            isGUIReady: false,
          },
        };
      } else {
        return {
          ...state,
          onGoingGuide: undefined,
        };
      }
    }
  } else {
    return state;
  }
}

function stopGuide(state: GuideState): GuideState {
  return {
    ...state,
    onGoingGuide: undefined,
  };
}
